import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createTicketCategory,
  getApiErrorMessage,
  getTicketCategory,
  updateTicketCategoryDefinition,
} from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TicketCategoryFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit =
    user && (isNew ? canWritePlatformConfig(user) : canEditPlatformConfig(user));

  useEffect(() => {
    if (isNew || !id) return;
    getTicketCategory(id)
      .then((c) => {
        setCode(c.code);
        setName(c.name);
        setDescription(c.description ?? '');
        setSortOrder(c.sortOrder);
        setIsActive(c.isActive);
        setIsDefault(c.isDefault);
        setIsSystem(c.isSystem);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await createTicketCategory({
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          sortOrder,
          isActive,
          isDefault,
        });
        navigate(PLATFORM_CONFIG.categories);
      } else if (id) {
        await updateTicketCategoryDefinition(id, {
          name: name.trim(),
          description: description.trim() || null,
          sortOrder,
          isActive,
          isDefault,
        });
        navigate(PLATFORM_CONFIG.categories);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) return <div className="alert alert-error">No permission.</div>;
  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <Link to={PLATFORM_CONFIG.categories} style={{ fontSize: '0.85rem' }}>
          ← Categories
        </Link>
        <h1>{isNew ? 'New category' : 'Edit category'}</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form className="card card-body" style={{ maxWidth: 560 }} onSubmit={handleSave}>
        <div className="form-group">
          <label>Code</label>
          <input
            className="form-control"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            disabled={!isNew}
          />
        </div>
        <div className="form-group">
          <label>Name</label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-control"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Sort order</label>
          <input
            type="number"
            min={0}
            className="form-control"
            style={{ maxWidth: 120 }}
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>
        <label>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />{' '}
          Active
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />{' '}
          Default category for new tickets
        </label>
        {isSystem && (
          <small style={{ display: 'block', marginTop: '0.35rem', color: 'var(--text-muted)' }}>
            Seeded system category — you can still rename, deactivate, or change the default.
          </small>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </>
  );
}
